import React, { useState, useEffect } from "react";
import CountryDropdown, {
  CountryOption,
} from "../core/Forms/DropdownComponent/DropdownModified";
import { COUNTRIES } from "../../constants/countries";
import { useUserStore } from "../../state";
import { BusinessType } from "../../types";
import { useCompanyStore } from "../../state/useCompanyStore";
import { SubscriptionBlueCheck } from "../../assets/icons";
import EsemieLogo from "../Images/EssemieBlack.svg";
import { fetchCountryCode } from "../../utils/fetchCountryCode";
import { showToast } from "../../utils/toastUtils";


interface BusinessTypeOption {
  value: BusinessType;
  label: string;
  description: string;
}
interface ShowFeatures {
  showBusinessType: boolean;
  showCountry: boolean;
  showBusinessName: boolean;
}
const businessTypes: BusinessTypeOption[] = [
  {
    value: "retail",
    label: "Retail Based Business",
    description:
      "I sell products directly to consumers through physical stores or platforms.",
  },
  {
    value: "service",
    label: "Service Based Business",
    description:
      "I offer services to clients, such as consulting, cleaning, or freelancing.",
  },
];

interface BusinessTypeSelectorProps {
  onClose: () => void;
  featureToUpdate?: ShowFeatures;
}

const BusinessTypeSelector: React.FC<BusinessTypeSelectorProps> = ({
  onClose,
  featureToUpdate,
}) => {
  const {
    showBusinessType = false,
    showCountry = false,
    showBusinessName = false,
  } = featureToUpdate || {};
  const { company: user, updateCompanyProfile } = useCompanyStore();
  const [businessName, setBusinessName] = useState<string>(
    user.businessName || "",
  );
  const [country, setCountry] = useState<string>(user.country || "");
  const [businessType, setBusinessType] = useState<string>(
    user.businessType || "",
  );

  const [businessNameError, setBusinessNameError] = useState(false);
  const [countryError, setCountryError] = useState(false);
  const [businessTypeError, setBusinessTypeError] = useState(false);

  const [countryData, setCountryData] = useState<CountryOption | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCountryChange = (option: CountryOption | null) => {
    if (option) {
      setCountry(option.iso2 || "");
      setCountryError(false);
    } else {
      setCountry("");
      setCountryError(true);
    }

    setCountryError(false);
  };
  useEffect(() => {
    const getCountryCode = async () => {
      try {
        if (country.trim() === "") {
          const data = await fetchCountryCode();
          setCountry(data.country_code);
        }
      } catch (err) {
        console.error("Failed to fetch country code.");
      }
    };

    getCountryCode();
  }, []);

  useEffect(() => {
    const foundCountry = COUNTRIES.find(
      (countryData) => countryData.iso2 === country,
    );
    setCountryData(foundCountry || null);
  }, [country]);

  // Update state when user data changes
  useEffect(() => {
    setBusinessName(user.businessName);
    setCountry(user?.country!);
    setBusinessType(user?.businessType!);
  }, [user]);

  const handleUpdate = async () => {
    try {
      setLoading(true);
      setError(null);
      await updateCompanyProfile({
        businessName,
        country,
        businessType,
      });
      setLoading(false);
      showToast("Business details updated successfully!", "success");
      onClose();
    } catch (error) {
      setLoading(false);
      setError("Failed to update profile, Please try again!");
      console.error(error);
    }
  };

  return (
    <>
      <div className="fixed inset-0 w-full h-full p-4 bg-gray-800 bg-opacity-80 flex items-center justify-center z-50">
        <div className="relative bg-white flex flex-col lg:w-[450px] max-lg:max-w-[400px] max-lg:min-w-[300px] lg:gap-4 gap-3 lg:p-5 p-4 lg:rounded-lg rounded-md shadow-md">
          <div className="flex justify-center lg:h-[30px] h-[24px]">
            <img src={EsemieLogo} alt="Esemie Logo" />
          </div>
          <div className="">
            <h2 className="text-center lg:text-[21px] text-[18px] font-semibold max-lg:text-[#0E2943]">
              Let's get your business set up.
            </h2>
            <p className="text-center lg:mt-[3px] text-[15px] max-lg:text-[14px] max-md:text-[12px] font-medium text-[#475467] ">
              Update your business details.
            </p>
          </div>

          {showBusinessName && (
            <div // Business Name Input
              className="relative mx-[1px]"
            >
              <input
                type="text"
                placeholder="Your business name"
                required
                value={businessName}
                onChange={(e) => {
                  setBusinessNameError(false), setBusinessName(e.target.value);
                }}
                className={`w-full px-2 lg:py-[10px] py-[6px] lg:rounded-md rounded-sm border-[#dfdfdf] lg:text-[14px] text-[12px] placeholder:text-gray-400 ${businessNameError && "border-red-500"}`}
              />
              {businessNameError && (
                <div className="absolute shaker lg:bottom-[-15px] bottom-[-13px] left-0 w-full text-red-500 lg:text-[12px] text-[10px]">
                  Enter a business name!
                </div>
              )}
            </div>
          )}

          {showCountry && (
            <div // Country Selection
              className="relative mx-[1px]"
            >
              <CountryDropdown
                options={COUNTRIES}
                onChange={handleCountryChange}
                defaultValue={countryData}
                invalidInputCheck={countryError}
                hasClassString={true}
                classString="w-full px-2 lg:py-[10px] py-[6px] border border-[#dfdfdf] lg:rounded-md rounded-sm lg:text-[14px] text-[12px]"
                placeholder="Select Country"
                showIcon={true}
                optionsClassString="lg:text-[14px] text-[12px]"
              />
              {countryError && (
                <div className="absolute shaker lg:bottom-[-15px] bottom-[-13px] left-0 w-full text-red-500 lg:text-[12px] text-[10px]">
                  Please select a Country!
                </div>
              )}
            </div>
          )}

          {showBusinessType && (
            <div // Dynamic Business Type Selection
              className={`relative flex flex-col lg:gap-4 gap-3 border ${businessTypeError ? "border-red-500" : "border-transparent"} lg:rounded-md rounded-sm`}
            >
              {businessTypes.map(({ value, label, description }) => (
                <div
                  key={value}
                  className={`relative w-full flex items-center border-2 border-[#EAECF0] px-2 lg:py-2 py-1 lg:rounded-md rounded-sm cursor-pointer hover:border-blue-200 hover:bg-blue-50 ${businessType === value && " border-blue-500"}`}
                  onClick={() => {
                    setBusinessTypeError(false), setBusinessType(value);
                  }}
                  data-testid={`${value}-business-selector`}
                >
                  <div className="lg:pr-3">
                    <h3 className="lg:text-[16px] text-[14px] font-medium text-slate-800">
                      {label}
                    </h3>
                    <p className="lg:text-[14px] text-[12px] font-normal text-[#475467]">
                      {description}
                    </p>
                  </div>
                  {businessType === value && (
                    <span className="absolute top-0 right-0">
                      <SubscriptionBlueCheck color="#0469c5" />
                    </span>
                  )}
                </div>
              ))}
              {businessTypeError && (
                <div className="absolute shaker lg:bottom-[-16px] bottom-[-14px] left-0 w-full  text-red-500 lg:text-[12px] text-[10px]">
                  Please select a business type!
                </div>
              )}
            </div>
          )}

          <div // Continue (Submit) Button
            className="lg:mt-3 mt-2"
          >
            <button
              className="text-white max-md:text-[14px] w-full lg:p-3 p-2 bg-[#044d97] hover:bg-[#0966c4] lg:rounded-md rounded-sm"
              onClick={() => {
                setBusinessNameError(
                  !businessName || businessName.trim() === "",
                );
                setCountryError(!country || country.trim() === "");
                setBusinessTypeError(
                  !businessType || businessType.trim() === "",
                );

                if (businessNameError === false && country && businessType) {
                  handleUpdate();
                }
              }}
            >
              Continue
            </button>
          </div>

          {/* Loading Overlay */}
          {loading && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
              <div className="loader border-t-4 border-blue-500 border-solid rounded-full w-8 h-8 animate-spin"></div>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
              <p className="lg:text-[14px] text-[12px] text-center text-red-500">
                {error}
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BusinessTypeSelector;
